import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {DropdownBehavior} from '../behaviors/dropdown-behavior.js';
import {InputSharedStylesHTML} from '../shared-styles/input.js';
import {PikadaySharedStyles} from '../shared-styles/pikaday.js';
import {PikadayCustomizationStyles} from '../shared-styles/pikaday-customizations.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {format, isValid, subDays, addDays, startOfMonth, lastDayOfMonth} from 'date-fns';
import {parseDate} from '@beauwest/business-date-parser';

class KeliDate extends ScopeBinding(HotkeyBehavior(DropdownBehavior(InputBehavior(PolymerElement)))) {
  static get template() {
    return html`
${InputSharedStylesHTML}

<keli-input-container id="container"
    label="[[caption]]"
    input-has-content="[[active]]"
    icon="date-range"
    always-float-label="[[masked]]"
    focused="[[focused]]"
    disabled="[[disabled]]"
    invalid="[[invalid]]"
    error-message="[[errorMessage]]"
    hotkey="[[formattedShortcutKey]]"
    chrome="[[chrome]]"
    help-text="[[helpText]]">

  <input type="text" id="input" value="[[currentValue]]" slot="input">
  <div slot="postscript">[[willParseTo]]</div>
</keli-input-container>`;
  }

  static get is() {
    return 'keli-date';
  }

  static get properties() {
    return {
      // @formatter:off
      computeWireFormatInitialization: Boolean,
      dataFormat: {type: String, value: 'yyyy-MM-dd'},
      dateTime: {type: Object, computed: 'computeDateTime(currentValue)'},
      keliControlTypes: {type: Array, value: ['date']},
      willParseTo: String,
      wireFormat: {type: String, value: 'M/d/y'}
      // @formatter:on
    }
  }

  get dropdownBehaviorStyles() {
    return `
${PikadaySharedStyles.cssText}
${PikadayCustomizationStyles.cssText}
`;
  }

  connectedCallback() {
    super.connectedCallback();

    this.dropdownElement.positionElement = this.$.container;
    this.addEventListener('keli-input-icon-activated', this.toggleDatePicker);

    // Create the Date picker
    this.datePickerInstance = new Pikaday({
      keyboardInput: false,
      bound: false,
      reposition: false,
      defaultDate: this.dateTime || new Date(),
      setDefaultDate: false,
      yearRange: 10,
      onSelect: selectedDate => {
        if (this.datePickerInstance && this.dropdownElement.visible()) {
          if (this.dateTime) {
            selectedDate.setHours(this.dateTime.getHours());
            selectedDate.setMinutes(this.dateTime.getMinutes());
            selectedDate.setSeconds((this.seconds ? this.dateTime.getSeconds() : 0));
          }
          this.setFromHotkey(this.toWireFormat(selectedDate));
          this.toggleDatePicker();
          this.$.input.focus();
        }
      },
      theme: `keli-${KeliInterface.Styles.palette.darkMode ? 'dark' : 'light'}`
    });
    this.dropdownElement.addContentElement(this.datePickerInstance.el);

    this.registerHotkey(KeliInterface.Hotkeys.date.current, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(new Date()));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.yesterday, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(subDays(new Date(), 1)));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.tomorrow, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(addDays(new Date(), 1)));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.first, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(startOfMonth(new Date())));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.last, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(lastDayOfMonth(new Date())));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.toggleCalendar, {
      down: () => {
        this.toggleDatePicker();
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.date.hide, {
      down: event => {
        if (this.dropdownElement && this.dropdownElement.visible()) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleDatePicker();
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.date.add, {
      down: event => {
        if (this.dateTime && !this.willParseTo) {
          event.preventDefault();
          event.stopPropagation();
          this.setFromHotkey(this.toWireFormat(addDays(this.dateTime, 1)));
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.date.subtract, {
      down: event => {
        if (this.dateTime && !this.willParseTo) {
          event.preventDefault();
          event.stopPropagation();
          this.setFromHotkey(this.toWireFormat(subDays(this.dateTime, 1)));
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keli-input-icon-activated', this.toggleDatePicker);

    if (this.datePickerInstance) {
      this.datePickerInstance.destroy();
      this.datePickerInstance = null;
    }
  }

  computeDateTime(currentValue) {
    if (!currentValue) {
      return undefined;
    }
    let parsed = parseDate(currentValue);
    return (isValid(parsed) ? parsed : undefined);
  }

  setFromHotkey(newValue) {
    if (newValue === this.currentValue) {
      this.currentValueChanged(newValue);
    } else {
      this.currentValue = newValue;
    }
    this.fireKeliAccepted = true;
    this.willParseTo = null;
    this._handleInput();
  }

  toWireFormat(newValue, wireFormat) {
    if (!newValue) {
      return null;
    }
    let parsed = parseDate(newValue);
    return (isValid(parsed) ? format(parsed, wireFormat || this.wireFormat) : undefined);
  }

  toggleDatePicker() {
    if (this.readOnly) {
      return;
    }
    if (this.datePickerInstance) {
      if (this.dropdownElement.visible()) {
        this.dropdownElement.hide();
      } else {
        if (this.dateTime) {
          this.datePickerInstance.setDate(this.dateTime, true);
        } else {
          this.datePickerInstance.gotoToday();
        }
        this.dropdownElement.show();
      }
    }
  }

  displayValueChanged(newValue) {
    if (newValue) {
      return this.toWireFormat(newValue);
    }
  }

  toRawFieldValue(value) {
    return this.toWireFormat(value, this.dataFormat);
  }

  currentValueChanged(newValue) {
    if (this.toRawFieldValue(this.displayValue) !== this.toRawFieldValue(newValue)) {
      this.$.input.value = (newValue ? this.toWireFormat(newValue) : '');
    }
    this.active = !!this.$.input.value;
    if (this.$.input.value) {
      this.willParseTo = undefined;
    }
  }

  handleInput() {
    this.active = !!this.$.input.value;
    let willParseToValue = this.toWireFormat(this.$.input.value);
    this.willParseTo = (willParseToValue && this.$.input.value !== this.currentValue ? `Will parse to: ${willParseToValue}` : undefined);
    if (this.$.input.value === '') {
      this.currentValue = this.$.input.value;
    }
  }

  handleFocus() {
    this.resetHotkeyState();
  }

  keliAccept() {
    const parsed = parseDate(this.$.input.value);
    if (parsed && isValid(parsed)) {
      this.currentValue = this.toWireFormat(parsed);
    }
    this.$.input.value = this.currentValue || '';
    this.active = !!this.$.input.value;
    this.willParseTo = '';
  }
}

customElements.define(KeliDate.is, KeliDate);
export {KeliDate};
